/**
 * API client for profile-update flow (magic link, profile-update JWT).
 * Does not use main app auth; token is passed explicitly.
 */

import axios from 'axios';
import { Person } from '../types';

const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;

const client = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

import type {
  ExperienceEntryInput,
  SkillInput,
  DomainInput,
  EducationInput,
  CertificationInput,
  LanguageInput,
} from '../types/candidateDetails';
import type { OverviewFieldsPatch } from './api';

export interface ValidateResponse {
  valid: boolean;
  personName?: string | null;
  expiresAt?: string;
  /** `employee` | `external_candidate` when valid */
  profileCategory?: string | null;
  isInternal?: boolean;
  error?: string;
}

export interface MeResponse {
  person: Person;
}

export interface ProfileUpdatePayload {
  token: string;
  title?: string;
  job_responsibilities?: string;
  expertise?: string;
  additional_skills?: string;
  ai_tool_experience?: string;
  languages?: string;
  professional_interests?: string;
  management_level?: string | null;
  aspiring_management_level?: string | null;
  skills_to_learn?: string | null;
  primary_location?: string;
  contact?: string;
  /** Only for external_candidate profiles (self-service). */
  linkedin_profile_url?: string | null;
}

export interface OntologySkill {
  id: string;
  name: string;
}

export const profileUpdateApi = {
  async getSkillsOntology(): Promise<OntologySkill[]> {
    const { data } = await client.get<{ skills: OntologySkill[] }>('/skills/ontology');
    return data.skills;
  },

  async validateToken(token: string): Promise<ValidateResponse> {
    const { data } = await client.get<ValidateResponse>('/profile-update/validate', {
      params: { token },
    });
    return data;
  },

  async googleLogin(idToken: string): Promise<{ token: string; user: { email: string; name: string; picture: string } }> {
    const { data } = await client.post<{ token: string; user: { email: string; name: string; picture: string } }>(
      '/auth/google-profile-update',
      { token: idToken }
    );
    return data;
  },

  async loadProfile(token: string, jwt: string): Promise<Person> {
    const { data } = await client.get<MeResponse>('/profile-update/me', {
      params: { token },
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data.person;
  },

  async loadSessionProfile(jwt: string): Promise<Person> {
    const { data } = await client.get<MeResponse>('/profile-update/session', {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data.person;
  },

  async submitProfile(payload: ProfileUpdatePayload, jwt: string): Promise<void> {
    await client.post(
      '/profile-update/submit',
      payload,
      { headers: { Authorization: `Bearer ${jwt}` } }
    );
  },

  async uploadCv(
    file: File,
    token: string | null | undefined,
    jwt: string
  ): Promise<CvIngestResult> {
    const form = new FormData();
    if (token?.trim()) form.append('token', token.trim());
    form.append('file', file);
    const { data } = await client.post<CvIngestResult>(
      '/profile-update/cv',
      form,
      {
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },

  async pasteCv(
    text: string,
    token: string | null | undefined,
    jwt: string
  ): Promise<CvIngestResult> {
    const body: { text: string; token?: string } = { text };
    if (token?.trim()) body.token = token.trim();
    const { data } = await client.post<CvIngestResult>(
      '/profile-update/paste',
      body,
      { headers: { Authorization: `Bearer ${jwt}` } }
    );
    return data;
  },

  async patchOverviewFields(token: string, jwt: string, payload: OverviewFieldsPatch): Promise<Person> {
    const { data } = await client.patch<Person>('/profile-update/me', { token, ...payload }, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data;
  },

  async replaceExperience(token: string, jwt: string, entries: ExperienceEntryInput[]): Promise<Person> {
    const { data } = await client.put<Person>('/profile-update/me/experience', { token, entries }, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data;
  },

  async replaceSkills(token: string, jwt: string, skills: SkillInput[]): Promise<Person> {
    const { data } = await client.put<Person>('/profile-update/me/skills', { token, skills }, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data;
  },

  async replaceDomains(token: string, jwt: string, domains: DomainInput[]): Promise<Person> {
    const { data } = await client.put<Person>('/profile-update/me/domains', { token, domains }, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data;
  },

  async replaceEducation(token: string, jwt: string, entries: EducationInput[]): Promise<Person> {
    const { data } = await client.put<Person>('/profile-update/me/education', { token, entries }, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data;
  },

  async replaceCertifications(token: string, jwt: string, entries: CertificationInput[]): Promise<Person> {
    const { data } = await client.put<Person>('/profile-update/me/certifications', { token, entries }, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data;
  },

  async replaceLanguages(token: string, jwt: string, languages: LanguageInput[]): Promise<Person> {
    const { data } = await client.put<Person>('/profile-update/me/languages', { token, languages }, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    return data;
  },
};

export interface CvIngestResult {
  success: true;
  person_id: number;
  review_status: 'unreviewed' | 'needs_review' | 'reviewed';
  parser_version: string;
  summary: {
    experience: number;
    education: number;
    certifications: number;
    languages: number;
    skills: number;
    target_roles: number;
    domains: number;
  };
}
